// System/audit timestamps are stored in UTC; business times (shift start/end,
// rostered hours, clock in/out) are stored in business-local time. These helpers
// render UTC system timestamps in the organisation's timezone.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const DEFAULT_ORG_TZ = "Australia/Brisbane";

let ORG_TZ = (typeof localStorage !== "undefined" && localStorage.getItem("orgTz")) || DEFAULT_ORG_TZ;

export const orgTz = () => ORG_TZ;

export function setOrgTz(tz) {
  if (!tz || tz === ORG_TZ) return;
  ORG_TZ = tz;
  try { localStorage.setItem("orgTz", tz); } catch { /* private mode */ }
}

// Parse a stored system timestamp into the org timezone. Strings carrying an
// explicit offset/Z are trusted; naive strings are treated as UTC.
function asOrg(v) {
  if (!v) return null;
  const s = String(v).trim();
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  const d = hasTz ? dayjs(s) : dayjs.utc(s.replace("T", " "));
  return d.isValid() ? d.tz(ORG_TZ) : null;
}

// "AEST" / "AWST" / "ACST" — en-AU gives the local abbreviation (Chrome's
// en-US fallback renders "GMT+10", which reads poorly for Australian users).
export function tzLabel(at) {
  try {
    const parts = new Intl.DateTimeFormat("en-AU", { timeZone: ORG_TZ, timeZoneName: "short" })
      .formatToParts(at ? new Date(at) : new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

// "14 Sep, 4:49 PM AEST"
export const fmtLog = (v) => {
  const d = asOrg(v);
  return d ? `${d.format("D MMM, h:mm A")} ${tzLabel(d.valueOf())}` : "—";
};

// Same, with the year — for audit tables spanning periods.
export const fmtLogFull = (v) => {
  const d = asOrg(v);
  return d ? `${d.format("D MMM YYYY, h:mm A")} ${tzLabel(d.valueOf())}` : "—";
};

// 24-hour compact form, no tz suffix — for tight rows where space is short.
export const fmtLogCompact = (v) => {
  const d = asOrg(v);
  return d ? d.format("D MMM, HH:mm") : "";
};

// Date only (no time/label).
export const fmtLogDate = (v) => {
  const d = asOrg(v);
  return d ? d.format("ddd D MMM") : "—";
};

// Date only with year, e.g. "13 Oct 2026" — for system timestamps (signed on, etc.).
export const fmtLogDay = (v) => {
  const d = asOrg(v);
  return d ? d.format("D MMM YYYY") : "—";
};

// ---- "Now" / "today" in the ORG timezone -------------------------------------
// Never use the browser's clock for business dates ("today", default date ranges,
// "is this overdue"): the viewer's device may be in another timezone, and
// new Date().toISOString() is UTC (a day behind for AU users before 10-11am).
export const orgNow = () => dayjs().tz(ORG_TZ);

// Today's calendar date in the org timezone, "YYYY-MM-DD".
export const orgToday = () => orgNow().format("YYYY-MM-DD");

// Org-local wall clock as a local-time dayjs (no tz conversion on later
// formatting), for pickers/anchors that work in plain local dates.
export const orgNowLocal = () => dayjs(orgNow().format("YYYY-MM-DDTHH:mm:ss"));

// Org-local "YYYY-MM-DD HH:mm:ss" — the format business-local times are stored in.
export const orgNowStamp = () => orgNow().format("YYYY-MM-DD HH:mm:ss");
